import { ID, SingleApiResponse } from '@ianesthesie-kit/services';
import axios, { AxiosResponse } from 'axios';
import { MedicalResponse } from '../baseMedical.service';
import { PatchableMedicalService } from '../patchableMedical.service';
import { SurgeryInfo } from './surgeryInfo.model';
import { isFuture, isToday } from 'date-fns';
import { parseDateOrNull } from '@ianesthesie-kit/core';
import { Emphasis } from '@/modules/surgicalEvent';

export class SurgeryInfoService extends PatchableMedicalService<SurgeryInfo> {
  private static INSTANCE_BY_ID: SurgeryInfoService;

  private constructor(patientId: string) {
    super('/surgery-info', patientId);
  }

  public static getInstance(patientId: ID): SurgeryInfoService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new SurgeryInfoService(patientId);
    }

    return this.INSTANCE_BY_ID;
  }

  public static isPreop(si?: SurgeryInfo): boolean {
    const surgeryDate = this.getSurgeryDate(si);
    if (!surgeryDate) {
      return false;
    }
    if (this.isEmergency(si)) {
      return isToday(surgeryDate) || isFuture(surgeryDate);
    }

    return isFuture(surgeryDate);
  }

  public static isPreopOrUnknownDate(si?: SurgeryInfo): boolean {
    return !si?.surgeryDate || this.isPreop(si);
  }

  public static isPostop(si?: SurgeryInfo): boolean {
    const surgeryDate = this.getSurgeryDate(si);
    return !!surgeryDate && !this.isPreop(si);
  }

  public static isCareTypeConflict(si: SurgeryInfo): boolean {
    if (!si) {
      return false;
    }
    const automationValue = si.automationCareType;
    const userValue = si.careType;

    return !!userValue && !!automationValue && userValue !== automationValue;
  }

  public static isEmergency(si?: SurgeryInfo): boolean {
    return si?.emergency === 'YES';
  }

  private static getSurgeryDate(si?: SurgeryInfo): Date | null {
    return parseDateOrNull(si?.surgeryDate);
  }

  public clearExtractedCcamCodes(): Promise<
    MedicalResponse<SurgeryInfo, false>
  > {
    return axios
      .delete(this.getUrl('extracted-ccam-codes'))
      .then((response: AxiosResponse<SingleApiResponse<SurgeryInfo>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public removeExtractedCcamCode(
    code: string
  ): Promise<MedicalResponse<SurgeryInfo, false>> {
    return axios
      .delete(this.getUrl(`extracted-ccam-codes/${code}`))
      .then((response: AxiosResponse<SingleApiResponse<SurgeryInfo>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public removeSurgeryAutomationCcamCode(
    code: string
  ): Promise<MedicalResponse<SurgeryInfo, false>> {
    return axios
      .delete(this.getUrl(`surgery-automation-ccam-codes/${code}`))
      .then((response: AxiosResponse<SingleApiResponse<SurgeryInfo>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  protected transformResponseEntity(data: any): SurgeryInfo {
    // care type will always be data.careType if present, or else it will default to automaticCareType
    // this is for the medicalSelectPatcher to work without any modifications
    // Since we go that way, it is important to calculate the careTypeConflict before this "mutation"
    return {
      ...super.transformResponseEntity(data),
      careType: data.careType ?? data.automationCareType,
      careTypeConflict: SurgeryInfoService.isCareTypeConflict(data)
    };
  }

  private getCareTypeEmphasis(surgeryInfo?: SurgeryInfo) {
    if (!surgeryInfo) {
      return;
    }
    const automationValue = surgeryInfo.automationCareType;
    const userValue = surgeryInfo.careType;

    if (userValue && automationValue && userValue !== automationValue) {
      return Emphasis.IMPORTANT;
    }

    return undefined;
  }
}
